// Activity events emitted by `content/articles`.
//
// Same conventions as `auth/events.ts`: dot-segment kind strings,
// `ARTICLE_ACTIVITY_KINDS` is the source-of-truth, every emit site
// references one of these constants.
//
// Payloads carry the minimum needed by downstream subscribers
// (notifications, calendar materialisation, search indexer). They include
// stable ids plus lightweight calendar/search snapshots so consumers do
// not cross module boundaries to read article tables directly. We still
// deliberately do NOT inline body content, secrets, pricing, or hashes.
//
// We DO inline `slug` because the calendar / notifications layers need
// to render a click-through URL without a DB hit on every event, and
// slug rotation is a known-deferred feature.
import { z } from 'zod';
import { ArticleStatus } from './article.js';
// ---------------------------------------------------------------------------
// Kind constants.
// ---------------------------------------------------------------------------
export const ARTICLE_ACTIVITY_KINDS = {
    Created: 'article.created',
    Updated: 'article.updated',
    Published: 'article.published',
    Scheduled: 'article.scheduled',
    /** Emitted when a `scheduled` article is reverted to `draft` before
     *  the publish-at instant fires (the unpublish route handles this
     *  case — there is no separate "cancel-schedule" route since the
     *  user-visible action is the same: take it back). Subscribers that
     *  materialised a calendar event for the scheduled go-live should
     *  remove it. */
    ScheduleCancelled: 'article.schedule-cancelled',
    Unpublished: 'article.unpublished',
    Archived: 'article.archived',
    GroupAttached: 'article.group-attached',
    GroupDetached: 'article.group-detached',
    /** Emitted when an authenticated reader completes a readable article.
     *  Used by community/engagement to award traceable reading-task points. */
    ReadCompleted: 'article.read.completed',
    /** A new row was inserted into `article_versions`. Fired by every
     *  content-changing path when the `articles.versions` flag is on:
     *  the auto-snapshot taken before applying an `updateArticle`
     *  patch, and the pre-restore snapshot taken before applying a
     *  `restoreVersion`. The `reason` payload field disambiguates. */
    VersionSaved: 'article.version-saved',
    /** A previous version's content was restored onto the article row.
     *  Subscribers may invalidate cached projections + reindex. */
    VersionRestored: 'article.version-restored',
};
/** Glob pattern matching every article-emitted kind. Used by subscribers
 *  that want a coarse "tell me about anything article-shaped" subscription
 *  (notifications dispatcher, future calendar source, search reindexer). */
export const ARTICLE_ACTIVITY_KIND_GLOB = 'article.*';
// ---------------------------------------------------------------------------
// Per-kind payload schemas. Wrapped by ActivityEvent at transport.
// ---------------------------------------------------------------------------
export const ArticleCreatedEvent = z.object({
    type: z.literal('article.created'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    /** Snapshot of taxonomy attachments at create time. Inlined so a
     *  subscriber (notifications, search reindex, calendar source)
     *  doesn't need to refetch the article row + run the join. May
     *  drift from current state if a later PATCH changes them — the
     *  subscriber should re-resolve through the LinkableKind for
     *  current shape. `categoryId` is `null` when the article is
     *  un-categorised. */
    categoryId: z.string().uuid().nullable(),
    tagSlugs: z.array(z.string()),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleUpdatedEvent = z.object({
    type: z.literal('article.updated'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    title: z.string(),
    status: ArticleStatus,
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    /** Closed list of top-level fields the request mutated. Lets a
     *  subscriber decide cheaply whether to reindex / refanout. */
    changedFields: z.array(z.string()),
    /** Snapshot of taxonomy attachments AFTER the update. When
     *  `changedFields` includes `categoryId` / `tags`, this is the new
     *  state; otherwise it's the unchanged current state. */
    categoryId: z.string().uuid().nullable(),
    tagSlugs: z.array(z.string()),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticlePublishedEvent = z.object({
    type: z.literal('article.published'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    slug: z.string(),
    publishedAt: z.string().datetime({ offset: true }),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleScheduledEvent = z.object({
    type: z.literal('article.scheduled'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    /** Future instant when the article will go live. The future calendar
     *  EventSource subscribes here to materialise an "article going live"
     *  CalendarEvent. */
    publishAt: z.string().datetime({ offset: true }),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleUnpublishedEvent = z.object({
    type: z.literal('article.unpublished'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleScheduleCancelledEvent = z.object({
    type: z.literal('article.schedule-cancelled'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    /** The publish-at instant that was cancelled. Subscribers that
     *  materialised this in a calendar / scheduler should look it up
     *  and remove it. */
    cancelledPublishAt: z.string().datetime({ offset: true }),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleArchivedEvent = z.object({
    type: z.literal('article.archived'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleGroupAttachedEvent = z.object({
    type: z.literal('article.group-attached'),
    articleId: z.string().uuid(),
    groupId: z.string().uuid(),
    byUserId: z.string().uuid(),
    title: z.string(),
    status: ArticleStatus,
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleGroupDetachedEvent = z.object({
    type: z.literal('article.group-detached'),
    articleId: z.string().uuid(),
    groupId: z.string().uuid(),
    byUserId: z.string().uuid(),
    title: z.string(),
    status: ArticleStatus,
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleReadCompletedEvent = z.object({
    type: z.literal('article.read.completed'),
    articleId: z.string().uuid(),
    readerId: z.string().uuid(),
    authorId: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    groupIds: z.array(z.string().uuid()),
    progress: z.number().min(0).max(100).nullable(),
    dwellSeconds: z.number().int().min(0).nullable(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleVersionSavedEvent = z.object({
    type: z.literal('article.version-saved'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    versionNo: z.number().int().positive(),
    /** 'edit' for auto-snapshots before an updateArticle patch;
     *  'restore-pre' for the snapshot taken before applying a
     *  restoreVersion. Mirrors the DB CHECK on
     *  `article_versions.reason`. */
    reason: z.enum(['edit', 'restore-pre']),
    occurredAt: z.string().datetime({ offset: true }),
});
export const ArticleVersionRestoredEvent = z.object({
    type: z.literal('article.version-restored'),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    byUserId: z.string().uuid(),
    /** Version number that was applied (the snapshot whose content is
     *  now live on the article row). */
    restoredVersionNo: z.number().int().positive(),
    /** Version number of the pre-restore snapshot the service captured
     *  before applying. The SDK uses this to render the "Undo restore"
     *  affordance — restoring this version reverts the operation. */
    preRestoreVersionNo: z.number().int().positive(),
    occurredAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=events.js.map